/**
 * Axios 请求实例配置
 * - baseURL: 来自 src/config（开发环境通过 webpack devServer proxy 转发到后端）
 * - withCredentials: true（自动携带 HttpOnly Cookie 中的 JWT Token）
 * - 响应拦截器：自动解包 response.data.data，业务层直接拿真实数据
 * - 401 自动刷新：Token 过期时自动调用 /auth/refresh，成功后重试原请求
 * - 并发请求排队：刷新期间的其他 401 请求排队等待，刷新成功后统一重试
 */
import axios, {AxiosError, InternalAxiosRequestConfig} from 'axios'
import type {ApiError, ApiResponse} from '@/types'
import {appConfig} from '@/config'

const request = axios.create({
  baseURL: appConfig.api.baseURL,
  timeout: 15000,
  withCredentials: true // 自动携带 HttpOnly Cookie
})

// 标记是否正在刷新 Token，防止并发刷新
let isRefreshing = false
// 刷新期间排队的请求
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}> = []

/** 刷新完成后，统一处理排队的请求 */
const processQueue = (error: unknown) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve()
    }
  })
  failedQueue = []
}

// 响应拦截器：
// - 常规业务接口返回 { data, timestamp }，这里统一解包成 data
// - /metrics 这类纯文本接口不会有 data 字段，直接返回原始 payload，避免页面拿到 undefined
request.interceptors.response.use(
  (response) => {
    const payload = response.data
    if (payload && typeof payload === 'object' && 'data' in payload && 'timestamp' in payload) {
      return (payload as ApiResponse<never>).data
    }

    return payload
  },
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    // 401 且非重试请求 → 尝试刷新 Token
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // 已有刷新在进行，当前请求排队等待
        return new Promise((resolve, reject) => {
          failedQueue.push({resolve, reject})
        }).then(() => request(originalRequest))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // 调用刷新接口（Cookie 中的 refreshToken 自动携带）
        await axios.post(appConfig.api.refreshURL, {}, {withCredentials: true})
        processQueue(null)
        return request(originalRequest) // 用新 Token 重试原请求
      } catch (refreshError) {
        processQueue(refreshError)
        // 刷新也失败 → 跳转登录页
        window.location.href = '/auth/login'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error.response?.data || error)
  }
)

export default request
